import { inject, injectable } from "inversify";
import { makeObservable, observable, runInAction } from "mobx";
import type { AppsNamesType, AppsWithMoreInfoType } from "@whatslly/types/AppsNames";
import { ActiveAction, ActiveActions } from "@whatslly/types/integrations/appsActions/AppsActions";
import type { IntegrationHttpRequestType } from "@whatslly/types/integrations/IntegrationHttpRequestType";
import type { UserType } from "@whatslly/types/User";
import { INTEGRATION_RESPONSE_ERROR_TYPES } from "@whatslly/types/integrations/IntegrationErrorsResponse";
import type { ParserActionType } from "@whatslly/types/UI-V2/JsonParser/ParserAction";
import type { IntegrationDataType } from "@whatslly/types/UI-V2/JsonParser/IntegrationDataType";
import { AppStorage } from "@whatslly/commons/appStorage";
import { AppStore } from "./AppStore";
import { ConfirmModal } from "@whatslly/ui-components/modals/ConfirmModal";
import { TFunction } from "i18next";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { modalService } from "@whatslly/ui-components/modals";
import { backgroundClient } from "@whatslly/commons/background/client";
import { BgCommands } from "@whatslly/commons/bgCommands";

type RequestResponseType = { success: true; token: string; join_url?: string; id?: string; host_email: string };
type RequestErrorResponseType = { success: false; error: string; errorCode?: string; user?: UserType };

@injectable()
export class IntegrationsStore {
  activeIntegrations: Partial<ActiveActions> = {};
  integrationsActions: string[] = [];
  actions: ParserActionType[] = [];
  integrations: IntegrationDataType[] = [];

  constructor(
    @inject(AppStore) private appStore: AppStore,
    @inject(AppStorage) private appStorage: AppStorage
  ) {
    makeObservable(this, {
      actions: observable.ref,
      integrations: observable.ref,
      activeIntegrations: observable.ref,
      integrationsActions: observable.ref,
    });
  }

  private getIntegrationsActions(activeIntegrations: Partial<ActiveActions>) {
    return Object.entries(activeIntegrations).reduce<string[]>((activeActionsToReturn, [, integration]) => {
      if (typeof integration !== "boolean") {
        integration.actions?.forEach((action) => activeActionsToReturn.push(action));
      }
      return activeActionsToReturn;
    }, []);
  }

  async refreshIntegrations(): Promise<{
    activeIntegrations: Partial<ActiveActions>;
    actions: ParserActionType[];
    integrations: IntegrationDataType[];
  }> {
    await this.checkIfIntegrationHasPermissions();
    const activeIntegrations = await this.appStorage.getActiveApps();
    const fullIntegrationsData = await this.getActions(activeIntegrations);
    await this.filterNotActiveIntegrationsActions(fullIntegrationsData.integrationsActionsByName, activeIntegrations);
    const integrationActions = this.getIntegrationsActions(activeIntegrations);
    runInAction(() => {
      this.activeIntegrations = activeIntegrations;
      this.integrationsActions = integrationActions;
      this.actions = fullIntegrationsData.actionsToReturn || [];
      this.integrations = fullIntegrationsData.integrationsData || [];
    });
    return { activeIntegrations, integrations: fullIntegrationsData.integrationsData || [], actions: fullIntegrationsData.actionsToReturn || [] };
  }

  async removeIntegrationFromStorage(appName: AppsNamesType): Promise<void> {
    const token = await this.getAppByName(appName);

    if (!token || Object.keys(token).length === 0) {
      return;
    }
    await backgroundClient.send(BgCommands.HTTP_REQUEST, {
      method: "POST",
      urlSuffix: `/integrations/auth/revoke`,
      token: token?.token ?? "",
      body: JSON.stringify({
        token: token?.token ?? "",
        appName,
      }),
    });
    this.actions.forEach((action) => {
      if (action.integrationName === appName) {
        favoriteActionsToRemove.push(action.functionName);
      }
    });
    const favoriteActionsToRemove = Object.entries(this.activeIntegrations).reduce<string[]>(
      (favoriteActionsToRemoveArray, [appNameToCheck, data]) => {
        if (typeof data !== "boolean" && appName === appNameToCheck) {
          data.actions?.forEach((actionName) => {
            favoriteActionsToRemoveArray.push(actionName);
          });
        }
        return favoriteActionsToRemoveArray;
      },
      []
    );
    for (const actionName of favoriteActionsToRemove) {
      await this.appStore.removeFavoriteAction(actionName);
    }
    await this.removeActiveApps(appName);
    await this.refreshIntegrations();
  }

  async checkIfIntegrationHasPermissions() {
    const activeApps = await this.appStorage.getActiveApps();
    const actionsToRemove: AppsNamesType[] = [];
    for (const appName in activeApps) {
      const integration = activeApps[appName as AppsWithMoreInfoType];
      if (integration?.actions && integration?.actions.length === 0) {
        actionsToRemove.push(appName as AppsNamesType);
        await this.removeActiveApps(appName as AppsNamesType);
      }
    }
    return actionsToRemove.length === 0;
  }

  getAppByName(appName: AppsNamesType) {
    return this.appStorage.getAppByName(appName);
  }

  async removeActiveApps(appName: AppsNamesType) {
    return this.appStorage.removeActiveApps(appName);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async addActiveApp<T extends Record<string, any>>(appName: AppsNamesType, data: Partial<ActiveAction> & T) {
    return this.appStorage.addActiveApp(appName, data);
  }

  async integrationHttpRequest<R>(data: IntegrationHttpRequestType): Promise<(RequestResponseType & R) | undefined> {
    const appData = await this.getAppByName(data.appName);
    try {
      const dataToSend: any = (() => {
        if (data.method === "GET") {
          const urlSuffix = data.query
            ? `/integrations/${data.appName}/${data.endPoint}?${new URLSearchParams(data.query).toString()}`
            : `/integrations/${data.appName}/${data.endPoint}`;
          return {
            method: data.method,
            urlSuffix,
            token: appData.token,
          };
        }
        return {
          method: data.method,
          urlSuffix: `/integrations/${data.appName}/${data.endPoint}`,
          token: appData.token,
          body: JSON.stringify(data.body),
        };
      })();
      const res = await backgroundClient.send<typeof BgCommands.HTTP_REQUEST, (RequestResponseType & R) | RequestErrorResponseType>(
        BgCommands.HTTP_REQUEST,
        dataToSend
      );
      if (res.success) {
        await this.addActiveApp(data.appName, { ...appData, token: res.token });
        return res;
      }
      // FIXME add logger after Flogger is ready
      if (res.error === INTEGRATION_RESPONSE_ERROR_TYPES.FATAL_ERROR) {
        await this.removeActiveApps(data.appName);
      }
      if (data.navigateToErrorPage) {
        data.navigateToErrorPage(data.appName, res.error);
      }
      return;
    } catch (error) {
      // FIXME add logger after Flogger is ready
      if (data.navigateToErrorPage) {
        data.navigateToErrorPage(data.appName, INTEGRATION_RESPONSE_ERROR_TYPES.RETRY_ERROR);
      }
      return;
    }
  }

  private async getActions(activeIntegrations: Partial<ActiveActions>) {
    const fullIntegrationsData = await backgroundClient.send<
      typeof BgCommands.HTTP_REQUEST,
      {
        actionsToReturn: ParserActionType[];
        integrationsData: IntegrationDataType[];
        integrationsActionsByName: Record<AppsNamesType, string[]>;
      }
    >(BgCommands.HTTP_REQUEST, {
      urlSuffix: `/integrations/builder/v2/actions?activeIntegrations=${JSON.stringify(Object.keys(activeIntegrations))}`,
      method: "GET",
    });

    return fullIntegrationsData;
  }

  async filterNotActiveIntegrationsActions(
    integrationsActionsByName: Record<AppsNamesType, string[]>,
    activeIntegrations: Record<AppsWithMoreInfoType, ActiveAction>
  ) {
    if (!integrationsActionsByName) {
      return;
    }

    for (const appName in integrationsActionsByName) {
      const appActions = integrationsActionsByName[appName as AppsNamesType];
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      if (activeIntegrations && !activeIntegrations[appName as AppsNamesType]) {
        for (let index = 0; index < appActions.length; index++) {
          const actionName = appActions[index];
          if (this.appStore.actionsMenuFavorites.includes(actionName)) {
            await this.appStore.tempRemoveForAnimation(actionName);
            this.appStore.removeFavoriteAction(actionName);
          }
        }
      }
    }
  }

  async onIntegrationDisconnect({ appName, userStore, t }: { appName: AppsNamesType; userStore: IUserStore; t: TFunction }) {
    if (userStore.user?.userId !== undefined && userStore?.phone !== undefined) {
      const modalPromise = await modalService.openModal(ConfirmModal, {
        okText: t("modals.remove_integration.remove"),
        title: t("modals.remove_integration.header"),
        body: t("modals.remove_integration.sub_header"),
      }).promise;

      if (!modalPromise?.result) {
        return;
      }
      backgroundClient.send(BgCommands.TRK, {
        action: `integration-revoked`,
        traits: {
          appName,
        },
      });

      await this.removeIntegrationFromStorage(appName);
    }
  }
}
